import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import { alpha } from '@mui/material/styles';
import { initiateShopifyAuth } from '../services/shopifyApi';

const AddStoreModal = ({ open, onClose }) => {
  const [shopDomain, setShopDomain] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInstall = async (e) => {
    e.preventDefault();
    setError('');

    if (!shopDomain.trim()) {
      setError('Please enter your store URL');
      return;
    }

    setLoading(true);

    try {
      const { authUrl } = await initiateShopifyAuth(shopDomain);
      window.location.href = authUrl;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0]?.message ||
        'Failed to connect to store. Please try again.';
      setError(message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setShopDomain('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <Box component="form" onSubmit={handleInstall}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha('#00D9A6', 0.12),
                color: '#00D9A6',
              }}
            >
              <StorefrontRounded />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Connect Shopify Store
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your store URL to begin installation
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Store URL"
            placeholder="mystore.myshopify.com"
            value={shopDomain}
            onChange={(e) => setShopDomain(e.target.value)}
            required
            autoFocus
            helperText="Enter your Shopify store domain (e.g., mystore.myshopify.com)"
            disabled={loading}
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={loading} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !shopDomain.trim()}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Install App'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddStoreModal;
